import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../sessionSlice";
import { fetchThemeData } from "../../themeSlice";
import { apiUrl } from "../../../utils/apiUrl";
import { s3ThemeAssetUrl } from "../../../utils/themeAssets";
import { getActiveGame, GAME_CATALOG, getGameDisplayName } from "../../../utils/gameHelpers";
import "./yogaUploads.css";

const PAGE_SIZE = 10;

function nonEmpty(value) {
  return String(value ?? "").trim() !== "";
}

function rowEmployeeId(row) {
  return String(row.employee_id || row.employeeId || "").trim();
}

function matchesSearch(row, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const name = String(row.name || "").toLowerCase();
  const email = String(row.email || "").toLowerCase();
  const employeeId = rowEmployeeId(row).toLowerCase();
  const status = row.visibleInGallery ? "visible" : "hidden";
  const likes = String(row.likeCount ?? "");

  return (
    name.includes(q) ||
    email.includes(q) ||
    employeeId.includes(q) ||
    status.includes(q) ||
    likes.includes(q)
  );
}

export default function YogaUploads() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData, currentTheme } = useSelector((s) => s.theme);
  const activeGame = getActiveGame(themeData);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [gameFilter, setGameFilter] = useState(activeGame || "all");

  useEffect(() => {
    setGameFilter(activeGame || "all");
  }, [activeGame]);

  const loadUploads = useCallback(async () => {
    if (!adminToken) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(apiUrl("/adminYogaUploads"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ activeGame, gameFilter }),
      });
      const payload = await resp.json();
      if (resp.ok && payload?.success) {
        setUploads(Array.isArray(payload.uploads) ? payload.uploads : []);
      } else {
        setUploads([]);
      }
    } catch (e) {
      console.error("adminYogaUploads", e);
      setUploads([]);
    } finally {
      setLoading(false);
    }
  }, [adminToken, activeGame, gameFilter]);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  useEffect(() => {
    setPage(1);
  }, [search, gameFilter]);

  const filteredUploads = useMemo(
    () => uploads.filter((row) => matchesSearch(row, search)),
    [uploads, search]
  );

  const totalPages = Math.max(1, Math.ceil(filteredUploads.length / PAGE_SIZE));

  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredUploads.slice(start, start + PAGE_SIZE);
  }, [filteredUploads, page]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const downloadCsv = () => {
    const hasName = filteredUploads.some((r) => nonEmpty(r.name));
    const hasEmail = filteredUploads.some((r) => nonEmpty(r.email));
    const hasEmployeeId = filteredUploads.some((r) => nonEmpty(rowEmployeeId(r)));

    const headers = [
      ...(hasName ? ["Name"] : []),
      ...(hasEmail ? ["Email"] : []),
      ...(hasEmployeeId ? ["Employee ID"] : []),
      "Likes",
      "Gallery",
      "Files",
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filteredUploads.map((r) => {
      const cols = [];
      if (hasName) cols.push(r.name || "");
      if (hasEmail) cols.push(r.email || "");
      if (hasEmployeeId) cols.push(rowEmployeeId(r));
      cols.push(
        r.likeCount,
        r.visibleInGallery ? "Visible" : "Hidden",
        (r.files || []).map((f) => s3ThemeAssetUrl(f.filename)).join(" | ")
      );
      return cols.map(esc).join(",");
    });
    const csv = "\uFEFF" + [headers.map(esc).join(","), ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `uploads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleVisibility = async (row) => {
    if (!adminToken || togglingId) return;
    const nextStatus = row.visibleInGallery ? 0 : 1;
    setTogglingId(row.id);
    try {
      const resp = await fetch(apiUrl("/adminYogaUploadVisibility"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ gameId: row.id, uploadStatus: nextStatus }),
      });
      const payload = await resp.json();
      if (!resp.ok || !payload?.success) {
        throw new Error(payload?.error || "Update failed");
      }
      setUploads((prev) =>
        prev.map((u) =>
          u.id === row.id
            ? { ...u, uploadStatus: nextStatus, visibleInGallery: nextStatus === 1 }
            : u
        )
      );
    } catch (e) {
      Swal.fire("Error", e.message || "Could not update visibility.", "error");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="yau-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back to rules
        </button>
      </div>

      <h1 className="yau-title">Gallery uploads</h1>
      <p className="yau-sub">
        Showing uploads for{" "}
        <strong>
          {gameFilter === "all" ? "all games" : getGameDisplayName(gameFilter)}
        </strong>
        . Hide uploads from the public gallery without deleting files.
      </p>

      {!loading ? (
        <div className="yau-toolbar">
          <select
            className="yau-filter"
            value={gameFilter}
            onChange={(e) => setGameFilter(e.target.value)}
            aria-label="Filter by game"
          >
            <option value={activeGame}>Active game ({getGameDisplayName(activeGame)})</option>
            <option value="all">All games</option>
            {GAME_CATALOG.filter((g) => g.id !== activeGame).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          {uploads.length > 0 ? (
            <>
          <input
            type="search"
            className="yau-search"
            placeholder="Search by name, email, visibility, likes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search uploads"
          />
          <span className="yau-count">
            {filteredUploads.length} of {uploads.length} upload{uploads.length === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            className="yau-download-btn"
            onClick={downloadCsv}
            disabled={filteredUploads.length === 0}
          >
            <i className="fa-solid fa-download" /> Download CSV
          </button>
            </>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="load-theme-container">
          <div className="load-theme-spinner" />
        </div>
      ) : uploads.length === 0 ? (
        <p className="yau-empty">No player uploads yet for this game.</p>
      ) : filteredUploads.length === 0 ? (
        <p className="yau-empty">No uploads match your search.</p>
      ) : (
        <>
          <div className="yau-table-wrap">
            <table className="yau-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Files</th>
                  <th>Likes</th>
                  <th>Gallery</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((row) => (
                  <tr key={row.id} className={row.visibleInGallery ? "" : "yau-row--hidden"}>
                    <td>
                      <div className="yau-player">
                        {row.name || row.email || rowEmployeeId(row) || "—"}
                      </div>
                    </td>
                    <td>
                      <div className="yau-files">
                        {(row.files || []).map((f) => (
                          <a
                            key={f.filename}
                            href={s3ThemeAssetUrl(f.filename)}
                            target="_blank"
                            rel="noreferrer"
                            className="yau-file-link"
                          >
                            {f.mediaType === "video" ? "▶ Video" : "🖼 Image"}
                          </a>
                        ))}
                      </div>
                    </td>
                    <td>{row.likeCount}</td>
                    <td>
                      <span
                        className={`yau-badge${row.visibleInGallery ? " yau-badge--on" : " yau-badge--off"}`}
                      >
                        {row.visibleInGallery ? "Visible" : "Hidden"}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="yau-toggle-btn"
                        disabled={togglingId === row.id}
                        onClick={() => toggleVisibility(row)}
                      >
                        {row.visibleInGallery ? "Hide" : "Show"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <nav className="yau-pagination" aria-label="Upload pages">
              <button
                type="button"
                className="yau-page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              <span className="yau-page-info">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="yau-page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </nav>
          ) : null}
        </>
      )}
    </div>
  );
}
